import { useEffect, useState } from 'react';
import { Loader2, ShieldOff, MonitorSmartphone } from 'lucide-react';
import {
  useTrustedDevicePolicy,
  useUpdateTrustedDevicePolicy,
  useRevokeAllTrustedDevices,
} from '../../hooks/useAdminData';
import type { TrustedDevicePolicyInput } from '../../types';

/**
 * Platform-wide rules for "Husk denne enheten" (skipping the 2FA code on a remembered
 * browser). GlobalAdmin only; the API enforces that, this card just hides itself otherwise.
 *
 * The three switches map 1:1 to Lynki.Auth's TrustedDevicePolicy. "Tilbakekall alle" is
 * the emergency lever: every remembered browser on the platform is forgotten at once.
 */
export function TrustedDevicePolicyCard() {
  const { data: policy, isLoading, isError } = useTrustedDevicePolicy();
  const update = useUpdateTrustedDevicePolicy();
  const revokeAll = useRevokeAllTrustedDevices();

  const [form, setForm] = useState<TrustedDevicePolicyInput | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);

  // Seed the form from the server once, and again after every successful save.
  useEffect(() => {
    if (policy) {
      setForm({
        enabled: policy.enabled,
        lifetimeDays: policy.lifetimeDays,
        requireCodeForAdmins: policy.requireCodeForAdmins,
      });
    }
  }, [policy]);

  const min = policy?.minLifetimeDays ?? 1;
  const max = policy?.maxLifetimeDays ?? 365;
  const lifetimeValid = form !== null && Number.isInteger(form.lifetimeDays) && form.lifetimeDays >= min && form.lifetimeDays <= max;
  const dirty =
    form !== null &&
    policy !== undefined &&
    (form.enabled !== policy.enabled ||
      form.lifetimeDays !== policy.lifetimeDays ||
      form.requireCodeForAdmins !== policy.requireCodeForAdmins);

  const handleSave = async () => {
    if (!form || !lifetimeValid) return;
    setMessage(null);
    try {
      await update.mutateAsync(form);
      setMessage('Innstillingene er lagret og gjelder umiddelbart.');
    } catch {
      setMessage('Kunne ikke lagre innstillingene. Prøv igjen.');
    }
  };

  const handleRevokeAll = async () => {
    setMessage(null);
    try {
      const result = await revokeAll.mutateAsync();
      setMessage(result.message || `${result.revoked} enheter tilbakekalt.`);
    } catch {
      setMessage('Kunne ikke tilbakekalle enhetene. Prøv igjen.');
    } finally {
      setConfirmingRevoke(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 lg:col-span-2">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
          <MonitorSmartphone size={20} className="text-amber-700" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Tofaktor: huskede enheter</h2>
          <p className="text-sm text-slate-500">
            Styrer «Husk denne enheten» på kodeskjermen. Gjelder alle brukere på plattformen.
          </p>
        </div>
      </div>

      {isLoading || !form ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 size={16} className="animate-spin" />
          {isError ? 'Kunne ikke hente innstillingene.' : 'Laster innstillinger...'}
        </div>
      ) : (
        <div className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              className="mt-1 rounded border-slate-300"
            />
            <span>
              <span className="block text-slate-800 font-medium">Tillat «Husk denne enheten»</span>
              <span className="block text-sm text-slate-500">
                Av: alle må skrive inn engangskode ved hver innlogging, også på enheter som allerede er husket.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3">
            <input
              type="number"
              min={min}
              max={max}
              value={form.lifetimeDays}
              onChange={(e) => setForm({ ...form, lifetimeDays: Number(e.target.value) })}
              disabled={!form.enabled}
              className="w-24 px-3 py-1.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
            />
            <span>
              <span className="block text-slate-800 font-medium">Levetid i dager</span>
              <span className="block text-sm text-slate-500">
                Hvor lenge en husket enhet slipper koden, regnet fra da den ble husket ({min}–{max}).
                Enheter som allerede er husket beholder levetiden de fikk.
              </span>
              {!lifetimeValid && (
                <span className="block text-sm text-red-600">Må være et heltall mellom {min} og {max}.</span>
              )}
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.requireCodeForAdmins}
              onChange={(e) => setForm({ ...form, requireCodeForAdmins: e.target.checked })}
              disabled={!form.enabled}
              className="mt-1 rounded border-slate-300"
            />
            <span>
              <span className="block text-slate-800 font-medium">Administratorer må alltid skrive inn kode</span>
              <span className="block text-sm text-slate-500">
                Kontoer med GlobalAdmin, UserAdmin, LicenseAdmin eller CustomerAdmin får aldri hoppe over
                koden, uansett husket enhet. Gjelder også deg.
              </span>
            </span>
          </label>

          {message && (
            <p role="status" className="text-sm text-slate-600">
              {message}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={handleSave}
              disabled={!dirty || !lifetimeValid || update.isPending}
              className="px-4 py-2 rounded-lg bg-slate-900 text-white font-medium hover:bg-slate-800 disabled:opacity-50"
            >
              {update.isPending ? 'Lagrer...' : 'Lagre'}
            </button>

            <span className="flex-1" />

            {confirmingRevoke ? (
              <span className="flex items-center gap-2 text-sm">
                <span className="text-slate-700">Alle brukere må skrive inn kode neste gang. Sikker?</span>
                <button
                  type="button"
                  onClick={handleRevokeAll}
                  disabled={revokeAll.isPending}
                  className="px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {revokeAll.isPending ? 'Tilbakekaller...' : 'Ja, tilbakekall alle'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingRevoke(false)}
                  disabled={revokeAll.isPending}
                  className="px-3 py-1.5 rounded-lg text-slate-600 hover:bg-slate-100"
                >
                  Avbryt
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingRevoke(true)}
                className="inline-flex items-center gap-1.5 text-sm text-red-700 hover:text-red-800"
              >
                <ShieldOff size={16} /> Tilbakekall alle huskede enheter
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TrustedDevicePolicyCard;
